<template>
  <div :class="selector"
       class="translate-field">
    <div v-if="useHeader" class="translate-field__head">
      <h3 v-if="title" class="translate-field__title">{{title}}
        <span v-if="isAttributeTrue($attrs.required)"
              class="input__required"> *</span>
        <slot name="title-append"></slot>
      </h3>

      <div class="translate-field__head-switches">
        <h3 v-if="label" class="translate-field__label">{{label}}
          <span v-if="isAttributeTrue($attrs.required)"
                class="input__required"> *</span>

          <SwitchCheckbox
            v-if="disableToggleText"
            :value="!disabled"
            :label="disableToggleText"
            class="_blue"
            @input="$emit('update:disabled', $event)"
          />
        </h3>
        <div
          class="translate-field__translates  translates-btns"
          :class="{ '_with-buttons-footer': originalLocale }"
        >
          <div v-if="useTranslateButtons" class="translates-btns__list">
            <button v-for="key in keys"
                    :key="key"
                    @click.prevent="onClick(key)"
                    :class="currentComputed === key ? '_active' : null"
                    class="translates-btns__btn  reset-button">
              <span class="translates-btns__btn-text">{{key | capitalize}}</span>
              <span v-if="isFilled(key)"
                    class="translates-btns__tick">
              <BaseSvg name="ico-tick"/>
            </span>

              <span
                v-if="originalLocale && compareStrings(originalLocale, key)"
                class="translates-btns__original-locale"
              >
              {{ $tVacancy('original') }}
            </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex items-center">
      <div>
        <slot name="prepend"></slot>
      </div>

      <div class="full-width">
        <div v-if="editor && !disabled"
             :key="currentComputed"
             class="translate-field__content">
          <slot v-bind="{key: currentComputed, value: computedValue, onInput}">
            <div v-for="(computedValueItem, computedValueIndex) in computedValue"
                 :key="computedValueIndex"
                 class="translate-field__input-wrap">
              <button v-if="withFullDelete && computedValueIndex === 0"
                      @click.prevent="onFullDelete"
                      class="translate-field__delete  base-btn">
                <BaseSvg name="ico-close"/>
              </button>
              <button v-if="computedValueIndex !== 0 && computedValue.length > 1"
                      @click.prevent="onDelete(computedValueIndex)"
                      class="translate-field__delete  base-btn">
                <BaseSvg name="ico-close"/>
              </button>
              <Editor @input="onInput(currentComputed, computedValueIndex, $event)"
                      @blur="onBlur(currentComputed, computedValueIndex, $event)"
                      :value="computedValueItem[currentComputed]"
                      :readonly="readonly"
                      :placeholder="placeholder"
                      :use-headings="useEditorHeadings"
                      class="translate-field__input"/>
            </div>
          </slot>
        </div>
        <div v-else-if="!disabled"
             :key="currentComputed"
             class="translate-field__content">
          <slot v-bind="{key: currentComputed, value: computedValue, onInput, onDelete}">
            <div v-for="(computedValueItem, computedValueIndex) in computedValue"
                 :key="computedValueIndex"
                 :class="textarea ? '_textarea' : null"
                 class="translate-field__input-wrap">
              <button v-if="withFullDelete && computedValueIndex === 0"
                      @click.prevent="onFullDelete"
                      class="translate-field__delete  base-btn">
                <BaseSvg name="ico-close"/>
              </button>
              <button v-if="computedValueIndex !== 0 && computedValue.length > 1"
                      @click.prevent="onDelete(computedValueIndex)"
                      class="translate-field__delete  base-btn">
                <BaseSvg name="ico-close"/>
              </button>
              <textarea v-if="textarea"
                        v-bind="$attrs"
                        @input="onInput(currentComputed, computedValueIndex, $event.target.value)"
                        @blur="onBlur(currentComputed, computedValueIndex, $event.target.value)"
                        :value="computedValueItem[currentComputed]"
                        :readonly="readonly"
                        class="translate-field__input"></textarea>
              <input v-else
                     v-bind="$attrs"
                     @input="onInput(currentComputed, computedValueIndex, $event.target.value)"
                     @blur="onBlur(currentComputed, computedValueIndex, $event.target.value)"
                     :value="computedValueItem[currentComputed]"
                     :readonly="readonly"
                     class="translate-field__input">

            </div>
          </slot>
        </div>
        <InputError :value="error"/>
      </div>
    </div>
  </div>
</template>

<script>
  import InputError from './BaseInputError'
  import SwitchCheckbox from './SwitchCheckbox';

  const Editor = () => import(
      /* webpackChunkName: "editor" */ '@/components/Editor'
      );

  export default {
    inheritAttrs: false,
    components: {
      Editor,
      InputError,
      SwitchCheckbox,
    },
    props: {
      value: {
        type: [String, Array, Object]
      },
      title: String,
      label: String,
      placeholder: String,
      editor: {
        type: Boolean,
        default: false
      },
      readonly: {
        type: Boolean,
        default: false
      },
      textarea: {
        type: Boolean,
        default: false
      },
      multiple: {
        type: Boolean,
        default: false
      },
      withFullDelete: {
        type: Boolean,
        default: false
      },
      error: {
        type: [String, Array]
      },
      noJSON: {
        type: Boolean,
        default: false
      },
      useHeader: {
        type: Boolean,
        default: true,
      },
      useTranslateButtons: {
        type: Boolean,
        default: true,
      },
      originalLocale: {
        type: String,
        default: null,
      },
      useEditorHeadings: {
        type: Boolean,
        default: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      disableToggleText: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        current: null
      }
    },
    computed: {
      keys() {
        let keys = [...Object.keys(this.$store.state.locales)]
        const currentLocale = this.$store.state.currentLocale
        let index = keys.findIndex(l => l === currentLocale)
        if(index > -1) {
          let v = keys.splice(index, 1)
          keys = v.concat(keys)
        }
        return keys
      },
      currentComputed() {
        return this.current || this.keys[0]
      },
      computedValue() {
        let value

        if(this.noJSON) {
          value = this.value || [{}]
        } else {
          value = JSON.parse(this.value || '[{}]')
        }

        if (!Array.isArray(value)) {
          value = [value]
        }
        return value.map(item => {
          item = item || {}

          return this.keys.reduce((obj, key) => {
            obj[key] = item[key] || ''
            return obj
          }, {})
        })
      },
      selector() {
        return {
          '_with-title': this.title,
          '_with-label': this.label,
          '_error': this.error
        }
      }
    },
    methods: {
      onClick(key) {
        this.current = key
      },
      onBlur(currentComputed, index, value) {
        let newValue = this.computedValue.map(item => {
          return Object.assign({}, item)
        })

        newValue[index][currentComputed] = value || ''

        if (!this.multiple) {
          newValue = newValue[0]
        }

        if(this.noJSON) {
          this.$emit('blur', newValue)
        } else {
          this.$emit('blur', JSON.stringify(newValue))
        }
      },
      onInput(currentComputed, index, value) {
        let newValue = this.computedValue.map(item => {
          return Object.assign({}, item)
        })

        newValue[index][currentComputed] = value || ''

        if (!this.multiple) {
          newValue = newValue[0]
        }

        if(this.noJSON) {
          this.$emit('input', newValue)
        } else {
          this.$emit('input', JSON.stringify(newValue))
        }
      },
      onDelete(index) {
        const newValue = this.computedValue.filter((e, i) => i !== index)

        if(this.noJSON) {
          this.$emit('input', newValue)
        } else {
          this.$emit('input', JSON.stringify(newValue))
        }
      },
      onFullDelete() {
        this.$emit('delete')
      },
      compareStrings(str1, str2) {
        return str1.toLowerCase() === str2.toLowerCase();
      },
      isFilled(key) {
        return this.computedValue.some(item => {
          return !!item[key]
        })
      },
      isAttributeTrue(attr) {
        return attr || attr === ''
      }
    }
  }
</script>

<style lang="less">

</style>
